import { useState } from 'react';
import { Modal } from '@/components/dialogs/Modal';
import { AsyncButton } from '@/components/common/AsyncButton';

interface EditProviderDialogProps {
  show: boolean;
  oldProvider: string;
  newProvider: string;
  // Submits the approval request; must throw on failure so the error toast shows.
  onSubmit: (comment: string) => Promise<void>;
  onClose: () => void;
}

const WARNING =
  'This action is irreversible and will affect the alerts arisen. Are you sure you want to edit ? ' +
  "If so, kindly add below the reason of this action and proceed to address this modification manually " +
  "on the provider's assets page for prompt resolution.";

// AS-IS port of EditProviderModal.cshtml: warning paragraph + old→new provider badges +
// required Comment + "Submit for approval". The provider change is a maker-checker request,
// not a direct update.
export function EditProviderDialog({ show, oldProvider, newProvider, onSubmit, onClose }: EditProviderDialogProps) {
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);

  const trimmed = comment.trim();
  const invalid = trimmed.length === 0;

  const submit = async () => {
    setTouched(true);
    if (invalid) return;
    await onSubmit(trimmed);
  };

  return (
    <Modal
      show={show}
      title="Edit Provider"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-dark btn-sm" onClick={onClose}>
            Cancel
          </button>
          <AsyncButton className="btn btn-primary btn-sm" onClick={submit}>
            Submit for approval
          </AsyncButton>
        </>
      }
    >
      <p className="text-muted">{WARNING}</p>

      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="badge bg-dark-transparent">{oldProvider || '-'}</span>
        <i className="fa fa-arrow-right" aria-hidden="true" />
        <span className="badge bg-primary-transparent">{newProvider}</span>
      </div>

      <div>
        <label htmlFor="mt-edit-provider-comment" className="form-label">
          Comment
        </label>
        <textarea
          id="mt-edit-provider-comment"
          className={`form-control${touched && invalid ? ' is-invalid' : ''}`}
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => setTouched(true)}
        />
        {touched && invalid && <div className="invalid-feedback d-block">A reason is required.</div>}
      </div>
    </Modal>
  );
}
